import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { PembayaranAirAset } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"
import { formatRupiah } from "@/lib/utils"

const postData = async (newData: PembayaranAirAset) => {
  const response = await api.post("/master/pembayaran-air-aset/store", newData)
  return response.data
}

const patchData = async (newData: PembayaranAirAset) => {
  const response = await api.patch(
    "/master/pembayaran-air-aset/update",
    newData
  )
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_aset_pembayaran_air: number
}) => {
  const response = await api({
    url: "/master/pembayaran-air-aset/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: PembayaranAirAset | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<PembayaranAirAset>({
    id_ref_aset_pembayaran_air: selectedRow?.id_ref_aset_pembayaran_air || 0,
    aset_pembayaran_air_start: selectedRow?.aset_pembayaran_air_start as string,
    aset_pembayaran_air_end: selectedRow?.aset_pembayaran_air_end as string,
    aset_pembayaran_air_text: selectedRow?.aset_pembayaran_air_text || "",
    aset_pembayaran_air_skor: selectedRow?.aset_pembayaran_air_skor || 0,
    is_aktif: selectedRow?.is_aktif ?? true,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listPembayaranAirAset"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Kategori Pembayaran Air"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.aset_pembayaran_air_start} ${selectedRow?.aset_pembayaran_air_end}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Kategori Pembayaran Air"
            : "Update Data Kategori Pembayaran Air"
        }
      />
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <Label>Mulai Dari (nilai)</Label>
          <Input
            placeholder="Ketik kategori pembayaran air (angka)..."
            value={form.aset_pembayaran_air_start}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                aset_pembayaran_air_start: event.target.value,
                aset_pembayaran_air_text: `${formatRupiah(
                  Number(event.target.value)
                )} - ${
                  prev.aset_pembayaran_air_end === ""
                    ? ""
                    : formatRupiah(Number(prev.aset_pembayaran_air_end))
                }`,
              }))
            }
          />
        </div>
        <div className="flex-1">
          <Label>Hingga (nilai)</Label>
          <Input
            placeholder="Ketik kategori pembayaran air (angka)..."
            value={form.aset_pembayaran_air_end}
            onChange={(event) => {
              if (event.target.value == "~") {
                setForm((prev) => ({
                  ...prev,
                  aset_pembayaran_air_end: event.target.value,
                  aset_pembayaran_air_text: `>= ${
                    prev.aset_pembayaran_air_start === ""
                      ? ""
                      : formatRupiah(Number(prev.aset_pembayaran_air_start))
                  }`,
                }))
              } else {
                setForm((prev) => ({
                  ...prev,
                  aset_pembayaran_air_end: event.target.value,
                  aset_pembayaran_air_text: `${
                    prev.aset_pembayaran_air_start === ""
                      ? ""
                      : formatRupiah(Number(prev.aset_pembayaran_air_start))
                  } - ${formatRupiah(Number(event.target.value))}`,
                }))
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label>Nilai Pembayaran Air (text)</Label>
        <Input
          placeholder="Ketik nilai pembayaran air (text)..."
          value={form.aset_pembayaran_air_text}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              aset_pembayaran_air_text: event.target.value,
            }))
          }
        />
      </div>

      <div>
        <Label>Skor</Label>
        <Input
          type="number"
          placeholder="Ketik skor..."
          value={form.aset_pembayaran_air_skor}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              aset_pembayaran_air_skor: Number(event.target.value),
            }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={form.is_aktif}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_aktif: !prev.is_aktif,
            }))
          }
        />
        <Label htmlFor="status">Status (Nonaktif/Aktif)</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
